import React, { useState, useEffect } from "react";
import styled from "styled-components";
import { useTranslation } from "react-i18next";
import { Tooltip } from "antd";
import { RiFileEditLine } from "react-icons/ri";
import { LiaTruckLoadingSolid, LiaTruckMovingSolid, LiaWarehouseSolid } from "react-icons/lia";
import { IoDocumentsOutline, IoExitOutline } from "react-icons/io5";

const MainContainer = styled.div`
  width: 100%;
  max-width: 400px;
  margin: 0 auto;
`;

const StepContainer = styled.div`
  display: flex;
  justify-content: space-between;
  margin-top: 2px;
  position: relative;

  :after {
    content: "";
    position: absolute;
    background: #4a154b;
    height: 4px;
    width: ${({ width }) => width};
    top: 50%;
    transition: 0.4s ease;
    transform: translateY(-50%);
    left: 0;
  }
`;

const StepWrapper = styled.div`
  position: relative;
  z-index: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  flex: 0 1 ${({ stepCount }) => 100 / stepCount}%;
  min-width: 0;
`;

const StepStyle = styled.div`
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background-color: var(--chakra-colors-gandolapp-clean);
  border: 3px solid
    ${({ hasAnomaly, step }) => {
      if (hasAnomaly) return "var(--chakra-colors-gandolapp-warning)";
      if (step === "completed") return "#1CE783";
      return "#F3E7F3";
    }};
  transition: 0.4s ease;
  display: flex;
  justify-content: center;
  align-items: center;
`;

const StepCount = styled.span`
  font-size: 12px;
  color: #f3e7f3;
  @media (max-width: 600px) {
    font-size: 8px;
  }
`;

const StepsLabelContainer = styled.div`
  margin-top: 4px;
  text-align: center;
`;

const StepLabel = styled.span`
  font-size: 8px;
  color: #4a154b;
  display: block;
  margin-top: 2px;
`;

const ButtonsContainer = styled.div`
  display: flex;
  justify-content: space-between;
  padding: 0 8px;
  margin-top: 10px;
  @media (max-width: 600px) {
    flex-direction: column;
    align-items: center;
  }
`;

const CheckMark = styled.div`
  font-size: 26px;
  font-weight: 600;
  color: #4a154b;
  transform: scaleX(-1) rotate(-46deg);
`;

const formatFirestoreDate = (date) => {
  if (!date) return null;
  let jsDate = null;
  if (date instanceof Date) {
    jsDate = date;
  } else if (typeof date.toDate === "function") {
    jsDate = date.toDate();
  } else if (typeof date._seconds === "number") {
    const nanos = typeof date._nanoseconds === "number" ? date._nanoseconds : 0;
    jsDate = new Date(date._seconds * 1000 + Math.floor(nanos / 1e6));
  } else if (typeof date.seconds === "number") {
    const nanos = typeof date.nanoseconds === "number" ? date.nanoseconds : 0;
    jsDate = new Date(date.seconds * 1000 + Math.floor(nanos / 1e6));
  } else if (typeof date === "number" || typeof date === "string") {
    const parsed = new Date(date);
    if (!Number.isNaN(parsed.getTime())) jsDate = parsed;
  }
  if (!jsDate) return null;
  const day = jsDate.getDate().toString().padStart(2, "0");
  const month = (jsDate.getMonth() + 1).toString().padStart(2, "0");
  const year = jsDate.getFullYear();
  const hours = jsDate.getHours().toString().padStart(2, "0");
  const minutes = jsDate.getMinutes().toString().padStart(2, "0");
  return `${day}/${month}/${year} ${hours}:${minutes}`;
};

const ProgressStep = (props) => {
  const [activeStep, setActiveStep] = useState(1);
  const [steps, setSteps] = useState([]);
  const { t } = useTranslation();

  const generateSteps = (type) => {
    const baseSteps =
      type === "CONTAINER_OUTBOUND" || type === "CONTAINER_INBOUND"
        ? [
            {
              icon: <RiFileEditLine size={15} color="var(--chakra-colors-gandolapp-secondary)" />,
              key: "BRIEF",
              step: 1,
              tooltip: "BRIEF",
            },
            {
              icon: (
                <LiaWarehouseSolid size={15} color="var(--chakra-colors-gandolapp-secondary)" />
              ),
              key: "DEPOT_OR_TERMINAL",
              step: 2,
              tooltip: "checkListContainer.depot",
            },
            {
              icon: <IoExitOutline size={15} color="var(--chakra-colors-gandolapp-secondary)" />,
              key: "GATING_OUT",
              step: 3,
              tooltip: "checkListContainer.gatingOut",
            },
            {
              icon: (
                <LiaTruckMovingSolid size={15} color="var(--chakra-colors-gandolapp-secondary)" />
              ),
              key: "IN_TRANSIT_TO_CUSTOMER",
              step: 4,
              tooltip: "checkListContainer.inTransitA",
            },
            {
              icon: (
                <IoDocumentsOutline size={15} color="var(--chakra-colors-gandolapp-secondary)" />
              ),
              key: "AT_CUSTOMER_DOCUMENTS",
              step: 5,
              tooltip: "checkListContainer.customerDocuments",
            },
            {
              icon: (
                <LiaTruckLoadingSolid size={15} color="var(--chakra-colors-gandolapp-secondary)" />
              ),
              key: "AT_CUSTOMER_LOADING_OR_UNLOADING",
              step: 6,
              tooltip: "checkListContainer.loading",
            },
            {
              icon: (
                <LiaTruckMovingSolid size={15} color="var(--chakra-colors-gandolapp-secondary)" />
              ),
              key: "IN_TRANSIT_TO_GATE_IN",
              step: 7,
              tooltip: "checkListContainer.inTransitB",
            },
            {
              icon: (
                <LiaWarehouseSolid size={15} color="var(--chakra-colors-gandolapp-secondary)" />
              ),
              key: "AT_DEPOT_OR_AT_GATED_IN",
              step: 8,
              tooltip: "checkListContainer.atDepo",
            },
            {
              icon: (
                <LiaTruckLoadingSolid size={15} color="var(--chakra-colors-gandolapp-secondary)" />
              ),
              key: "UNLOADED",
              step: 9,
              tooltip: "checkListContainer.unloaded",
            },
          ]
        : [
            {
              icon: <RiFileEditLine size={15} color="var(--chakra-colors-gandolapp-secondary)" />,
              key: "BRIEF",
              step: 1,
              tooltip: "BRIEF",
            },
            {
              icon: (
                <LiaTruckLoadingSolid size={15} color="var(--chakra-colors-gandolapp-secondary)" />
              ),
              key: "LOADING_POINT",
              step: 2,
              tooltip: "checkListConventional.loading",
            },
            {
              icon: (
                <LiaTruckMovingSolid size={15} color="var(--chakra-colors-gandolapp-secondary)" />
              ),
              key: "IN_TRANSIT_TO_CUSTOMER",
              step: 3,
              tooltip: "checkListConventional.inTransit",
            },
            {
              icon: <RiFileEditLine size={15} color="var(--chakra-colors-gandolapp-secondary)" />,
              key: "STRAIGHT_TO_CUSTOMER",
              step: 4,
              tooltip: "checkListConventional.straight",
            },
            {
              icon: <RiFileEditLine size={15} color="var(--chakra-colors-gandolapp-secondary)" />,
              key: "BREAK_REST",
              step: 5,
              tooltip: "checkListConventional.breakRest",
            },
            {
              icon: (
                <IoDocumentsOutline size={15} color="var(--chakra-colors-gandolapp-secondary)" />
              ),
              key: "AT_CUSTOMER_DOCUMENTS",
              step: 6,
              tooltip: "checkListConventional.customerDocuments",
            },
            {
              icon: (
                <LiaTruckLoadingSolid size={15} color="var(--chakra-colors-gandolapp-secondary)" />
              ),
              key: "AT_CUSTOMER_LOADING_OR_UNLOADING",
              step: 7,
              tooltip: "checkListConventional.unloading",
            },
            {
              icon: <RiFileEditLine size={15} color="var(--chakra-colors-gandolapp-secondary)" />,
              key: "AVAILABLE",
              step: 8,
              tooltip: "checkListConventional.available",
            },
          ];

    return baseSteps.map((step) => ({
      ...step,
      label: step.icon,
      tooltip: step.tooltip ? t(step.tooltip) : null,
      isCompleted: false,
      isConfirmed: false,
    }));
  };

  useEffect(() => {
    setSteps(generateSteps(props.type));
  }, [props.type, t]);

  useEffect(() => {
    if (props.records) {
      setSteps((prevSteps) =>
        prevSteps.map((stepObj) => {
          const stepObjectFound = props.records.find((step) => step.state === stepObj.key);
          const hasAnomaly = props.anomalies?.some((anomaly) => anomaly.newState === stepObj.key);

          if (stepObjectFound) {
            return {
              ...stepObj,
              isCompleted: true,
              hasAnomaly,
              isConfirmed: stepObjectFound.confirmed === "CONFIRMED_AFFIRMATIVE",
              date: stepObjectFound.date || null,
            };
          }

          if (hasAnomaly) {
            return {
              ...stepObj,
              hasAnomaly: true,
            };
          }

          return stepObj;
        })
      );
    }
  }, [props.records, props.anomalies]);

  useEffect(() => {
    if (!props.currentState || !steps.length) {
      return;
    }

    const nextIndex = steps.findIndex((step) => step.key === props.currentState);
    if (nextIndex >= 0) {
      setActiveStep(nextIndex + 1);
    }
  }, [props.currentState, steps]);

  useEffect(() => {
    if (props.activeStep && props.activeStep !== activeStep) {
      setActiveStep(props.activeStep);
    }
  }, [props.activeStep]);

  const width = `${(100 / (steps.length - 1)) * (activeStep - 1)}%`;

  return (
    <MainContainer>
      <StepContainer width={width}>
        {steps.map(({ step, label, tooltip, isCompleted, isConfirmed, date, hasAnomaly }) => (
          <StepWrapper key={step} stepCount={steps.length}>
            {tooltip ? (
              <Tooltip
                title={date ? `${tooltip} - ${formatFirestoreDate(date)}` : tooltip}
                arrow
              >
                <StepStyle step={isCompleted ? "completed" : "incomplete"} hasAnomaly={hasAnomaly}>
                  {isConfirmed ? <CheckMark>L</CheckMark> : <StepCount>{""}</StepCount>}
                </StepStyle>
              </Tooltip>
            ) : (
              <StepStyle step={isCompleted ? "completed" : "incomplete"} hasAnomaly={hasAnomaly}>
                {isConfirmed ? <CheckMark>L</CheckMark> : <StepCount>{""}</StepCount>}
              </StepStyle>
            )}
            <StepsLabelContainer>
              <StepLabel>{label}</StepLabel>
            </StepsLabelContainer>
          </StepWrapper>
        ))}
      </StepContainer>
      <ButtonsContainer />
    </MainContainer>
  );
};

export default ProgressStep;
